import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useVhAdminStore, VH_ADMIN_PAGE_SIZE } from '@/admin/admin.store';
import type { VhAdminUser } from '@/admin/admin.types';
import { initials, roleLabel, userContact, userRoles } from '@/admin/admin.users';
import { AdminBadge } from '@/admin/components/AdminBadge';
import { AdminButton } from '@/admin/components/AdminButton';
import { AdminCard, AdminCardList } from '@/admin/components/AdminCard';
import { AdminModal } from '@/admin/components/AdminModal';
import { AdminPagination } from '@/admin/components/AdminPagination';
import { AdminSearchInput } from '@/admin/components/AdminSearchInput';
import { AdminSelect } from '@/admin/components/AdminSelect';
import { AdminSpinner } from '@/admin/components/AdminSpinner';
import {
  AdminTable,
  AdminTableCell,
  AdminTableHead,
  AdminTableHeadCell,
  AdminTableRow,
} from '@/admin/components/AdminTable';
import styles from '@/admin/pages/AllUsersPage.module.scss';
import { UserDetailsPanel } from '@/admin/pages/UserDetailsPanel';
import { isSuperAdminRole, useVhAdminAccessStore } from '@/admin/vh-admin-access.store';
import { imageVariantLink } from '@/common/media';
import { AuthorizedImage } from '@/components/AuthorizedImage';
import { UserRole } from '@/utils/roles';
import { toast } from '@/utils/toast';

const ROLE_OPTIONS = Object.values(UserRole);

const userEmail = (user: VhAdminUser) => userContact(user, 'email');

const personName = (user: VhAdminUser) =>
  `${user.person_info.first_name ?? ''} ${user.person_info.last_name ?? ''}`.trim() ||
  'this person';

/**
 * Which named audience a person belongs to. This replaces the old beta-tester switch:
 * that flag is now derived from the audience, so a toggle could only ever pick between
 * the two built-in ones.
 */
const AudiencePicker = ({ user }: { user: VhAdminUser }) => {
  const audiences = useVhAdminStore((state) => state.audiences);
  const setUserAudience = useVhAdminStore((state) => state.setUserAudience);
  const { first_name, last_name } = user.person_info;

  const assign = async (audienceId: string) => {
    try {
      await setUserAudience(user.id, audienceId);
    } catch {
      toast.error("Couldn't change the audience. Try again");
    }
  };

  return (
    <AdminSelect
      value={user.beta_group_id}
      onChange={assign}
      label={`Audience for ${first_name} ${last_name}`}
    >
      {audiences.map((audience) => (
        <option key={audience.id} value={audience.id}>
          {audience.name}
        </option>
      ))}
    </AdminSelect>
  );
};

const UserIdentity = ({ user }: { user: VhAdminUser }) => {
  const { first_name, last_name } = user.person_info;

  return (
    <div className={styles.user}>
      {user.avatar ? (
        <AuthorizedImage className={styles.avatar} src={imageVariantLink(user.avatar, 28)} alt="" />
      ) : (
        <span className={styles.avatarFallback}>{initials(first_name, last_name)}</span>
      )}
      <span className={styles.name}>
        {first_name} {last_name}
      </span>
    </div>
  );
};

/** The person's AUTH role: user, admin or super admin. */
const SystemRoleBadge = ({ user }: { user: VhAdminUser }) => {
  const role = useVhAdminStore((state) => state.userSystemRoles[user.id]);

  if (!role) return <span className={styles.muted}>-</span>;

  return <AdminBadge tone={role === 'user' ? 'neutral' : 'success'}>{roleLabel(role)}</AdminBadge>;
};

/**
 * Grants or removes admin rights, for a super admin only. A super admin's role is managed in the
 * database, and nobody changes their own.
 */
const AdminRightsAction = ({
  user,
  onChange,
}: {
  user: VhAdminUser;
  onChange: (user: VhAdminUser, grant: boolean) => void;
}) => {
  const role = useVhAdminStore((state) => state.userSystemRoles[user.id]);
  const viewerId = useVhAdminAccessStore((state) => state.userId);
  const viewerRole = useVhAdminAccessStore((state) => state.role);

  if (!role || !isSuperAdminRole(viewerRole) || isSuperAdminRole(role) || user.id === viewerId) {
    return null;
  }

  const isAdmin = role === 'admin';

  return (
    <AdminButton
      size="sm"
      tone={isAdmin ? 'danger' : 'ghost'}
      onClick={() => onChange(user, !isAdmin)}
    >
      {isAdmin ? 'Remove Admin Rights' : 'Grant Admin Rights'}
    </AdminButton>
  );
};

const UserRoleBadges = ({ user }: { user: VhAdminUser }) => {
  const roles = userRoles(user);

  if (roles.length === 0) return <span className={styles.muted}>-</span>;

  return (
    <div className={styles.badges}>
      {roles.map((r) => (
        <AdminBadge key={r}>{roleLabel(r)}</AdminBadge>
      ))}
    </div>
  );
};

export const AllUsersPage = () => {
  const {
    users,
    usersCount,
    usersPage,
    usersLoading,
    audiences,
    fetchUsers,
    fetchAudiences,
    fetchUserById,
  } = useVhAdminStore();
  // `?user=<id>` opens that person's panel, e.g. from the Access List.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedUserId = searchParams.get('user');
  const [linkedUser, setLinkedUser] = useState<VhAdminUser | null>(null);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [audience, setAudience] = useState('');
  // Held by id, so the panel follows the row through an audience change or a refetch.
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  /** The user whose admin rights are about to change, and whether they gain or lose them. */
  const [roleChange, setRoleChange] = useState<{ user: VhAdminUser; grant: boolean } | null>(null);
  const [changingRole, setChangingRole] = useState(false);
  const setUserSystemRole = useVhAdminStore((state) => state.setUserSystemRole);
  const selectedUser =
    users.find((user) => user.id === selectedUserId) ??
    (linkedUser?.id === selectedUserId ? linkedUser : null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    fetchAudiences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!linkedUserId) return;

    setSelectedUserId(linkedUserId);
    let cancelled = false;
    fetchUserById(linkedUserId)
      .then((user) => {
        if (cancelled) return;
        if (!user) {
          toast.error('Couldn’t find this user. Please search for them by name.');
          return;
        }
        setLinkedUser(user);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) toast.error('Couldn’t open the user. Please try again.');
      });

    return () => {
      cancelled = true;
    };
  }, [linkedUserId, fetchUserById]);

  const confirmRoleChange = async () => {
    if (!roleChange) return;

    setChangingRole(true);
    try {
      await setUserSystemRole(roleChange.user.id, roleChange.grant ? 'admin' : 'user');
      toast.success(
        roleChange.grant ? 'They’ve been made an admin.' : 'Their admin rights have been removed.',
      );
      setRoleChange(null);
    } catch (err) {
      console.error(err);
      toast.error(
        roleChange.grant
          ? 'Couldn’t grant admin rights. Please try again.'
          : 'Couldn’t remove the admin rights. Please try again.',
      );
    } finally {
      setChangingRole(false);
    }
  };

  const openRoleChange = (user: VhAdminUser, grant: boolean) => setRoleChange({ user, grant });

  const closePanel = () => {
    setSelectedUserId(null);
    if (linkedUserId) {
      setSearchParams(
        (params) => {
          params.delete('user');
          return params;
        },
        { replace: true },
      );
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      fetchUsers({ page: 1, search, role, audience });
      return;
    }

    const timeout = setTimeout(() => {
      fetchUsers({ page: 1, search, role, audience });
    }, 300);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, role, audience]);

  return (
    <div>
      <div className={styles.header}>
        <h1 className={styles.title}>All Users</h1>
        <div className={styles.filters}>
          <AdminSelect value={role} onChange={setRole} label="Filter by role">
            <option value="">All roles</option>
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </AdminSelect>
          <AdminSelect value={audience} onChange={setAudience} label="Filter by audience">
            <option value="">All audiences</option>
            {audiences.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </AdminSelect>
          <AdminSearchInput value={search} onChange={setSearch} placeholder="Search by name…" />
        </div>
      </div>

      {usersLoading && users.length === 0 ? (
        <div className={styles.loading}>
          <AdminSpinner />
        </div>
      ) : users.length === 0 ? (
        <div className={styles.empty}>No users found</div>
      ) : (
        <>
          <div className={styles.desktopOnly}>
            <AdminTable>
              <AdminTableHead>
                <AdminTableHeadCell>User</AdminTableHeadCell>
                <AdminTableHeadCell>Contact</AdminTableHeadCell>
                <AdminTableHeadCell>Role</AdminTableHeadCell>
                <AdminTableHeadCell>Audience</AdminTableHeadCell>
                <AdminTableHeadCell>System Role</AdminTableHeadCell>
                <AdminTableHeadCell>{''}</AdminTableHeadCell>
              </AdminTableHead>
              <tbody>
                {users.map((user) => (
                  <AdminTableRow key={user.id} onActivate={() => setSelectedUserId(user.id)}>
                    <AdminTableCell>
                      <UserIdentity user={user} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <span className={styles.muted}>{userEmail(user) ?? '-'}</span>
                    </AdminTableCell>
                    <AdminTableCell>
                      <UserRoleBadges user={user} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <AudiencePicker user={user} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <SystemRoleBadge user={user} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <AdminRightsAction user={user} onChange={openRoleChange} />
                    </AdminTableCell>
                  </AdminTableRow>
                ))}
              </tbody>
            </AdminTable>
          </div>

          <div className={styles.mobileOnly}>
            <AdminCardList>
              {users.map((user) => (
                <AdminCard key={user.id} onActivate={() => setSelectedUserId(user.id)}>
                  <div className={styles.cardHeader}>
                    <UserIdentity user={user} />
                    <AudiencePicker user={user} />
                  </div>
                  <div className={styles.cardContact}>{userEmail(user) ?? '-'}</div>
                  <UserRoleBadges user={user} />
                  <div className={styles.cardSystemRole}>
                    <SystemRoleBadge user={user} />
                  </div>
                  <div className={styles.cardSystemRole}>
                    <AdminRightsAction user={user} onChange={openRoleChange} />
                  </div>
                </AdminCard>
              ))}
            </AdminCardList>
          </div>

          <AdminPagination
            page={usersPage}
            pageSize={VH_ADMIN_PAGE_SIZE}
            total={usersCount}
            onPageChange={(page) => fetchUsers({ page })}
          />
        </>
      )}

      <UserDetailsPanel user={selectedUser} onClose={closePanel} />

      {roleChange && (
        <AdminModal
          title={
            roleChange.grant
              ? `Grant admin rights to ${personName(roleChange.user)}?`
              : `Remove admin rights from ${personName(roleChange.user)}?`
          }
          onClose={() => setRoleChange(null)}
          footer={
            <>
              <AdminButton onClick={() => setRoleChange(null)} disabled={changingRole}>
                Cancel
              </AdminButton>
              <AdminButton
                tone={roleChange.grant ? 'primary' : 'danger'}
                onClick={confirmRoleChange}
                disabled={changingRole}
              >
                {roleChange.grant ? 'Grant Admin Rights' : 'Remove Admin Rights'}
              </AdminButton>
            </>
          }
        >
          <p>
            {roleChange.grant
              ? 'They’ll be able to open this admin area, manage every user, and change the access list.'
              : 'They’ll lose access to this admin area right away.'}
          </p>
        </AdminModal>
      )}
    </div>
  );
};
